import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useMockAuth } from '../contexts/MockAuthContext';
import { Gamepad2, CheckCircle, Loader2 } from 'lucide-react';

export default function MockAuthModal() {
  const { isAuthModalOpen, closeAuthModal, login } = useMockAuth();
  const tAuth = useTranslations('auth');
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // 动画控制
  useEffect(() => {
    if (isAuthModalOpen) {
      // 打开时延迟显示内容，制造弹出动画效果
      setTimeout(() => setShowContent(true), 50);
    } else {
      setShowContent(false);
      setIsAuthorizing(false);
      setIsSuccess(false);
    }
  }, [isAuthModalOpen]);

  const handleAuthorize = async () => {
    setIsAuthorizing(true);
    
    // 模拟授权过程
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsAuthorizing(false);
    setIsSuccess(true);
    
    // 显示成功后自动关闭
    await new Promise(resolve => setTimeout(resolve, 800));
    login();
  };

  if (!isAuthModalOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center">
      {/* 背景遮罩 */}
      <div 
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          showContent ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={closeAuthModal}
      />
      
      {/* 弹出内容 */}
      <div 
        className={`
          relative w-full max-w-[414px] bg-[#1a2535]
          rounded-t-3xl shadow-2xl border-t border-cyan-500/30
          transform transition-all duration-300 ease-out
          ${showContent ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
        `}
      >
        {/* 拖动指示条 */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
        </div>
        
        {/* 主内容 */}
        <div className="px-6 pb-8 pt-4">
          {/* Logo和标题 */}
          <div className="flex flex-col items-center mb-6">
            <div className={`
              w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-sky-500
              flex items-center justify-center mb-4 shadow-lg
              ${isAuthorizing ? 'animate-pulse' : ''}
              ${isSuccess ? 'from-emerald-400 to-green-500' : ''}
              transition-all duration-300
            `}>
              {isSuccess ? (
                <CheckCircle className="w-8 h-8 text-white" />
              ) : (
                <Gamepad2 className="w-8 h-8 text-white" />
              )}
            </div>
            
            <h2 className="text-white text-xl font-semibold mb-2">
              {isSuccess ? tAuth('loginSuccess') : tAuth('loginTitle')}
            </h2>
            
            <p className="text-gray-400 text-sm text-center">
              {isSuccess 
                ? tAuth('welcome')
                : tAuth('prompt')}
            </p>
          </div>

          {/* 提示信息 */}
          {!isSuccess && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div>
                  <p className="text-gray-200 text-sm font-medium">
                    {tAuth('mustLogin')}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {tAuth('authorizeHint')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 授权按钮 */}
          {!isSuccess && (
            <button
              onClick={handleAuthorize}
              disabled={isAuthorizing}
              className={`
                w-full py-4 rounded-xl font-semibold text-white shadow-lg
                transition-all duration-200
                ${isAuthorizing 
                  ? 'bg-gray-600 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-cyan-500 to-sky-500 hover:from-cyan-600 hover:to-sky-600 active:scale-[0.98]'
                }
              `}
            >
              {isAuthorizing ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {tAuth('authorizing')}
                </span>
              ) : (
                tAuth('authorize')
              )}
            </button>
          )}

          {/* 底部说明 */}
          {!isSuccess && (
            <p className="text-gray-500 text-xs text-center mt-4">
              {tAuth('terms')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
